/**
 * What the slash menu and the block handle offer, in the order they show it.
 *
 * Written out twice — here and in `BlockCommand.swift` — with the same labels
 * and the same search words, so `/tod` finds the same thing on the phone as on
 * the desktop. It is a menu, not a domain rule, so it has no fixture; keep the
 * two in step by hand.
 */
export type BlockKind =
  | 'text'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'bulleted'
  | 'numbered'
  | 'todo'
  | 'quote'
  | 'code';

export type BlockCommand = {
  id: BlockKind | 'divider' | 'image' | 'table';
  label: string;
  detail: string;
  keywords: string[];
};

export const blockCommands: BlockCommand[] = [
  { id: 'text', label: 'Text', detail: 'Plain text', keywords: ['paragraph', 'plain'] },
  {
    id: 'heading1',
    label: 'Heading 1',
    detail: 'Large section heading',
    keywords: ['h1', 'title'],
  },
  { id: 'heading2', label: 'Heading 2', detail: 'Medium section heading', keywords: ['h2'] },
  { id: 'heading3', label: 'Heading 3', detail: 'Small section heading', keywords: ['h3'] },
  {
    id: 'bulleted',
    label: 'Bulleted list',
    detail: 'A simple list',
    keywords: ['bullet', 'unordered', 'ul'],
  },
  {
    id: 'numbered',
    label: 'Numbered list',
    detail: 'A list with numbering',
    keywords: ['ordered', 'ol', '1.'],
  },
  {
    id: 'todo',
    label: 'To-do list',
    detail: 'Track tasks with a checklist',
    keywords: ['todo', 'task', 'checkbox', 'checklist'],
  },
  { id: 'quote', label: 'Quote', detail: 'Capture a quotation', keywords: ['blockquote'] },
  {
    id: 'code',
    label: 'Code',
    detail: 'Capture a code snippet',
    keywords: ['snippet', 'codeblock'],
  },
  {
    id: 'divider',
    label: 'Divider',
    detail: 'Visually divide blocks',
    keywords: ['hr', 'rule', 'line', 'separator'],
  },
  {
    id: 'image',
    label: 'Image',
    detail: 'Add a picture or screenshot',
    keywords: ['photo', 'picture', 'screenshot'],
  },
  {
    id: 'table',
    label: 'Table',
    detail: 'Add a typed table',
    keywords: ['grid', 'database', 'columns', 'rows'],
  },
];

/** The kinds a block can be turned into, which is the catalogue minus the inserted things. */
export const turnIntoKinds = blockCommands.filter(
  (command): command is BlockCommand & { id: BlockKind } =>
    !['divider', 'image', 'table'].includes(command.id),
);

/**
 * The commands a query finds. A word of the label or a search word that starts
 * with the query comes first, so `h` lists the headings before anything that
 * merely contains the letter.
 */
export function matchCommands(query: string): BlockCommand[] {
  const needle = query.trim().toLowerCase();

  if (!needle) {
    return blockCommands;
  }

  const rank = (command: BlockCommand) => {
    const label = command.label.toLowerCase();

    if (label.startsWith(needle)) {
      return 0;
    }

    if (label.split(' ').some(word => word.startsWith(needle))) {
      return 1;
    }

    if (command.keywords.some(word => word.startsWith(needle))) {
      return 2;
    }

    if (label.includes(needle) || command.keywords.some(word => word.includes(needle))) {
      return 3;
    }

    return null;
  };

  return blockCommands
    .map(command => ({ command, rank: rank(command) }))
    .filter((entry): entry is { command: BlockCommand; rank: number } => entry.rank !== null)
    .sort((a, b) => a.rank - b.rank)
    .map(entry => entry.command);
}

export const slashQueryLimit = 24;

/**
 * The command being typed: a slash at the start of the block or after a space,
 * and what follows it up to the caret.
 *
 * The query may hold spaces (`/bulleted list`) but not a second slash or a
 * line break, and it gives up after 24 characters — by then the owner is
 * writing a path or a fraction, not asking for a block.
 */
export function slashQuery(textBeforeCaret: string): { query: string; start: number } | null {
  const start = textBeforeCaret.lastIndexOf('/');

  if (start < 0 || (start > 0 && !/\s/.test(textBeforeCaret[start - 1]))) {
    return null;
  }

  const query = textBeforeCaret.slice(start + 1);

  if (query.includes('\n') || query.length > slashQueryLimit) {
    return null;
  }

  return { query, start };
}
